import type { Href } from "expo-router";
import { Redirect, Stack, usePathname, useSegments } from "expo-router";
import Head from "expo-router/head";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  OverviewShell,
  type ActiveProgramContext,
  type OverviewNavKey,
  type ProgramNavKey,
} from "../../components/client/OverviewShell";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { PANE_THEMES } from "../../components/brand/paneThemes";
import { useAuth } from "../../lib/authContext";
import { useIsLimitedClient } from "../../lib/clientPermissions";
import { buildClientIdentity } from "../../lib/clientIdentity";
import { getClientCompetition } from "../../lib/clientCompetitions";
import { isImpersonating } from "../../lib/impersonate";
import { ViewAsBanner } from "../../components/admin/ViewAsBanner";
import { PortalGate } from "../../lib/PortalGate";

// Cache the last-fetched program by id at module scope so re-entering a
// program (or hopping between its routes) renders the active-program
// section synchronously instead of flashing empty while the fetch runs.
const programCache = new Map<string, ActiveProgramContext>();

const THEME = PANE_THEMES.Client;

export default function ClientLayout() {
  const segments = useSegments();
  const pathname = usePathname();
  const { user } = useAuth();

  // Expo Router parent layouts don't see resolved dynamic params from child
  // segments (useLocalSearchParams returns `id: undefined` here on
  // /client/competition/<uuid>). Match the URL directly to recover the id —
  // also explicitly excludes the literal "/client/competition/new" route.
  const competitionMatch = pathname.match(/\/client\/competition\/([^\/]+)/);
  const competitionId =
    competitionMatch && competitionMatch[1] !== "new" ? competitionMatch[1] : null;
  const inProgram = competitionId !== null;

  // Segments for /client/competition/123/candidates look like
  // ['(client)', 'client', 'competition', '[id]', 'candidates'] — the
  // bracketed `[id]` placeholder is what comes back, not the resolved value.
  const last = segments[segments.length - 1] ?? "";

  let activeKey: OverviewNavKey | undefined;
  let activeProgramKey: ProgramNavKey | undefined;
  let topbarTitle = "Dashboard";

  // Student/team detail pages live conceptually inside the candidates flow,
  // so they should highlight Candidates in the OVERVIEW sidebar — not fall
  // through to the default Dashboard.
  const inStudentDetail = pathname.startsWith("/client/student/");
  const inTeamDetail = pathname.startsWith("/client/team/");

  if (inProgram) {
    activeKey = undefined;
    if (last === "edit") {
      activeProgramKey = "program-edit";
    } else if (last === "[id]" || last === "competition") {
      // Bare /client/competition/[id] redirects to /dashboard. During the
      // redirect tick, don't highlight anything.
      activeProgramKey = undefined;
    } else if (last === "dashboard") {
      activeProgramKey = "program-dashboard";
    } else if (last === "candidates") {
      activeProgramKey = "program-candidates";
    } else if (last === "deliverables" || last === "[deliverableId]" || last === "new") {
      activeProgramKey = "program-deliverables";
    } else if (last === "positions") {
      activeProgramKey = "program-positions";
    } else if (last === "pipeline") {
      activeProgramKey = "program-pipeline";
    } else if (last === "shortlist") {
      activeProgramKey = "program-shortlist";
    }
  } else if (inStudentDetail) {
    activeKey = "candidates";
    topbarTitle = "Candidate";
  } else if (inTeamDetail) {
    activeKey = "candidates";
    topbarTitle = "Team";
  } else if (last === "candidates") {
    activeKey = "candidates";
    topbarTitle = "Candidates";
  } else if (last === "company") {
    activeKey = "company";
    topbarTitle = "Your Company";
  } else if (last === "team") {
    activeKey = "team";
    topbarTitle = "Team";
  } else if (last === "billing") {
    activeKey = "billing";
    topbarTitle = "Billing";
  } else if (last === "settings") {
    activeKey = "settings";
    topbarTitle = "Settings";
  } else if (last === "notifications") {
    activeKey = undefined;
    topbarTitle = "Notifications";
  } else if (last === "new" && (segments as string[]).includes("competition")) {
    activeKey = undefined;
    topbarTitle = "Create program";
  } else if (last === "book" && (segments as string[]).includes("competition")) {
    // /client/competition/new/book — the Coravu RM discovery-call page.
    activeKey = undefined;
    topbarTitle = "Book a discovery call";
  } else if (last === "welcome") {
    activeKey = undefined;
    topbarTitle = "Welcome";
  } else {
    activeKey = "dashboard";
    topbarTitle = "Dashboard";
  }

  const [activeProgram, setActiveProgram] = useState<ActiveProgramContext | undefined>(() =>
    competitionId ? programCache.get(competitionId) : undefined
  );

  // Track the last id we requested so a quick swap between programs doesn't
  // race the slower fetch and stomp the new program with the old one.
  const lastRequestedId = useRef<string | null>(null);

  useEffect(() => {
    if (!competitionId) {
      setActiveProgram(undefined);
      lastRequestedId.current = null;
      return;
    }

    const cached = programCache.get(competitionId);
    if (cached) setActiveProgram(cached);

    lastRequestedId.current = competitionId;
    let cancelled = false;
    void getClientCompetition(competitionId).then((comp) => {
      if (cancelled || !comp) return;
      if (lastRequestedId.current !== competitionId) return;
      const ctx: ActiveProgramContext = {
        id: comp.id,
        name: comp.name,
        status: comp.status,
        engagement_model: comp.engagement_model === "rm" ? "rm" : "licensing",
      };
      programCache.set(competitionId, ctx);
      setActiveProgram(ctx);
    });
    return () => {
      cancelled = true;
    };
  }, [competitionId]);

  const finalTopbarTitle = inProgram
    ? activeProgram?.name ?? "Loading…"
    : topbarTitle;

  const identity = useMemo(() => buildClientIdentity(user), [user]);
  const { isLimited } = useIsLimitedClient();

  // Company-management surfaces a limited (former-Manager) client must never
  // reach. RLS already blocks every write here, but redirecting keeps them out
  // of pages that would render empty/erroring. Cosmetic guard; not the wall.
  const limitedBlocked =
    isLimited &&
    (last === "company" ||
      last === "team" ||
      last === "billing" ||
      (inProgram && last === "edit"));

  const impersonating = isImpersonating();

  if (limitedBlocked) {
    return <Redirect href={"/client" as Href} />;
  }

  return (
    <>
      <Head>
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      <PortalGate portal="client">
      {impersonating ? <ViewAsBanner theme={THEME} /> : null}
      <OverviewShell
        topbarTitle={finalTopbarTitle}
        identity={identity}
        activeKey={activeKey}
        activeProgram={activeProgram}
        activeProgramKey={activeProgramKey}
        limited={isLimited}
      >
        <ErrorBoundary boundaryName="client">
          <Stack
            screenOptions={{
              headerShown: false,
              animation: "none",
              contentStyle: { backgroundColor: THEME.pageBg },
            }}
          />
        </ErrorBoundary>
      </OverviewShell>
      </PortalGate>
    </>
  );
}

